import React, { useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Github } from "lucide-react";

const Projects = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const projects = [
    {
      title: "V2X Communication for Autonomous Vehicles",
      description: "Developed a reinforcement learning-based solution for autonomous vehicles using V2X communication to improve vehicle coordination, traffic density awareness, and decision-making in real-time.",
      technologies: ["Reinforcement Learning", "V2X", "Autonomous Vehicles"],
      links: { github: "#", demo: "#" },
      color: "blue"
    },
    {
      title: "Chat Counselor - Mental Health Assistant",
      description: "Built a voice-enabled mental health assistant designed to provide users with immediate support. Uses Llama model and AWS APIs for speech recognition and synthesis.",
      technologies: ["Llama AI", "AWS", "Speech Recognition", "Mental Health"],
      links: { github: "#", demo: "#" },
      color: "purple"
    },
    {
      title: "Landfill Management Platform",
      description: "Developed a landfill detection platform using computer vision and aerial imagery to classify and segment landfill areas with deep learning models.",
      technologies: ["Computer Vision", "MLOps", "Airflow", "MLflow", "DVC"],
      links: { github: "#", demo: "#" },
      color: "green"
    },
    {
      title: "Smart Cane for Visually Impaired",
      description: "Co-developed a smart cane that predicts the color and type of clothing to assist visually impaired individuals in shopping.",
      technologies: ["Edge Computing", "MLOps", "Moroccan Darija NLP"],
      links: { github: "#", demo: "#" },
      color: "amber"
    }
  ];

  const scrollLeft = () => {
    document.getElementById('projects-container').scrollBy({ left: -300, behavior: 'smooth' });
  };

  const scrollRight = () => {
    document.getElementById('projects-container').scrollBy({ left: 300, behavior: 'smooth' });
  };

  const getBgColor = (color, isHovered) => {
    const baseColors = {
      blue: "bg-blue-50 border-blue-200",
      purple: "bg-purple-50 border-purple-200",
      green: "bg-green-50 border-green-200",
      amber: "bg-amber-50 border-amber-200"
    };
    
    return baseColors[color] || "bg-gray-50 border-gray-200";
  };

  const getAccentColor = (color) => {
    const accentColors = {
      blue: "bg-blue-600",
      purple: "bg-purple-600",
      green: "bg-green-600",
      amber: "bg-amber-600"
    };
    
    return accentColors[color] || "bg-gray-600";
  };

  return (
    <section id="projects" className="py-16 px-4 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 relative">
            Projects
            <span className="absolute -bottom-3 left-0 w-24 h-1 bg-indigo-600"></span>
          </h2>
          
          <div className="hidden md:flex space-x-4">
            <button onClick={scrollLeft} className="p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-all duration-200">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={scrollRight} className="p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-all duration-200">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="relative">
          <div 
            id="projects-container"
            className="flex overflow-x-auto space-x-6 pb-8 pt-2 snap-x snap-mandatory scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {projects.map((project, index) => (
              <div 
                key={index}
                className={`flex-shrink-0 snap-center bg-white border-2 rounded-xl shadow-lg w-full md:w-96 
                  transform transition-all duration-300 ${hoveredIndex === index ? 'scale-105 -translate-y-2' : ''} 
                  ${getBgColor(project.color, hoveredIndex === index)}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div className={`h-2 w-full rounded-t-lg ${getAccentColor(project.color)}`}></div>
                
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    {project.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-6">
                    {project.description}
                  </p>
                  
                  <div className="mb-6 flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="text-xs font-medium py-1 px-2 bg-gray-100 text-gray-700 rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex justify-between items-center mt-auto">
                    <div className="flex space-x-3">
                      <a href={project.links.github} className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors">
                        <Github className="w-5 h-5 mr-1" />
                        <span className="text-sm">Code</span>
                      </a>
                      <a href={project.links.demo} className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors">
                        <ExternalLink className="w-5 h-5 mr-1" />
                        <span className="text-sm">Demo</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="md:hidden flex justify-center space-x-4 mt-6">
            <button onClick={scrollLeft} className="p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-all duration-200">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={scrollRight} className="p-2 rounded-full bg-white shadow-md hover:bg-gray-100 transition-all duration-200">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;